import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import MoneyField from "@/components/MoneyField";
import PhotoStep from "@/components/PhotoStep";
import CashCounter from "@/components/CashCounter";
import { CARDLINE, FLOAT, TERMINAL, TILL_LABEL, ZLINE, DENOMS, TIP_FLOAT_BOX, SHORTAGE_REASONS, SHORTAGE_CHECKS, GROUPS, DEPARTMENTS } from "@/lib/constants";
import { money } from "@/lib/format";
import { createClose, getName } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { AlertTriangle, CheckCircle2, ChevronRight, Share2, Copy, RotateCcw, Plus, MessageSquare } from "lucide-react";
import CoachDialog, { CoachButton } from "@/components/CoachDialog";
import StaffPicker from "@/components/StaffPicker";
import { toast } from "sonner";

const blank = {
  moneris_total: 0,
  tips: 0,
  register_drawer_total: 0,
  register_card_line: 0,
  register_cash_line: 0,
  tax_totals: { gst: 0, pst: 0, fd_pst: 0, liq_pst: 0 },
  denominations: Object.fromEntries(DENOMS.map((d) => [d.key, 0])),
  cheques: 0,
  gift_cards: 0,
  other: 0,
  notes: "",
  cash_tips_in_jar: null,
  float_counted_back: null,
  moneris_photo_b64: null,
  register_photo_b64: null,
  who_worked: "",
  tips_paid_out: false,
  shortage_reason: "",
  shortage_reason_other: "",
  shortage_checklist: {},
  needs_review: false,
  group_totals: {},
  groups_photo_b64: null,
  groups_photo_skip_reason: "",
  closed_on: "",
  float_in_count: true,
};

const STEPS_GRILL = ["intro", "moneris_photo", "moneris_confirm", "register_photo", "register_confirm", "departments", "card_check", "cash", "extras", "total", "reason", "checks", "who_worked", "summary"];
// Pro Shop till also rings restaurant items (beer/chips/pop brought down), so it gets the same departments step.
const STEPS_OTHER = STEPS_GRILL;

const STEPS = {
  end_of_day:   STEPS_GRILL,  // resolved at runtime based on till
  shift_change: STEPS_GRILL,
  morning:      ["cash", "morning_summary"],
  just_count:   ["cash", "just_count_summary"],
};

function StepShell({ title, hint, children, footer, testId }) {
  return (
    <div data-testid={testId} className="space-y-5">
      <div>
        <h2 className="text-2xl sm:text-3xl text-[#0D311E] tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
          {title}
        </h2>
        {hint && <p className="text-[#2B533E] mt-2">{hint}</p>}
      </div>
      <div>{children}</div>
      {footer && <div className="pt-2">{footer}</div>}
    </div>
  );
}

function PrimaryBtn({ children, onClick, testId, disabled }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className="w-full h-14 rounded-2xl bg-[#1A5D3A] text-white text-base font-medium shadow-sm hover:bg-[#13462A] active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2"
    >
      {children}
    </button>
  );
}

function GhostBtn({ children, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full h-12 rounded-xl text-[#2B533E] hover:bg-[#F5F2EA] text-sm"
    >
      {children}
    </button>
  );
}

export default function Wizard() {
  const { till: tillEnc, mode } = useParams();
  const till = decodeURIComponent(tillEnc);
  const nav = useNavigate();
  const baseFlow = STEPS[mode] || STEPS_GRILL;
  const flow = (mode === "end_of_day" || mode === "shift_change")
    ? (till === "Grill" ? STEPS_GRILL : STEPS_OTHER)
    : baseFlow;
  const [i, setI] = useState(0);
  const step = flow[i];
  const [data, setData] = useState(() => ({ ...blank, who_worked: getName() || "" }));
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [deptZeroWarned, setDeptZeroWarned] = useState(false);
  const [deptPhotoWarned, setDeptPhotoWarned] = useState(false);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const card_sales = useMemo(() => Math.round((data.moneris_total - data.tips) * 100) / 100, [data.moneris_total, data.tips]);
  const card_diff = useMemo(() => Math.round((card_sales - data.register_card_line) * 100) / 100, [card_sales, data.register_card_line]);
  const cash_counted = useMemo(
    () => Math.round(DENOMS.reduce((s, d) => s + (Number(data.denominations[d.key]) || 0) * d.value, 0) * 100) / 100,
    [data.denominations]
  );
  const floatAmt = FLOAT[till] || 300;
  const isCloseMode = mode === "end_of_day" || mode === "shift_change";
  const cash_deposit = isCloseMode ? Math.round((cash_counted - (data.float_in_count === false ? 0 : floatAmt)) * 100) / 100 : 0;
  const total_one = useMemo(
    () => Math.round((card_sales + cash_deposit + data.cheques + data.gift_cards + data.other) * 100) / 100,
    [card_sales, cash_deposit, data.cheques, data.gift_cards, data.other]
  );
  const difference = useMemo(
    () => Math.round((total_one - data.register_drawer_total) * 100) / 100,
    [total_one, data.register_drawer_total]
  );

  const next = () => setI((n) => Math.min(n + 1, flow.length - 1));
  const back = () => (i === 0 ? nav(-1) : setI((n) => n - 1));

  const startOver = () => {
    setData({ ...blank, who_worked: getName() || "" });
    setSavedId(null);
    setI(0);
  };

  // Fill the wizard with realistic sample numbers so the flow can be tested
  // end-to-end without a receipt and without typing. Values are chosen to
  // balance cleanly (difference ≈ $0) for the selected till.
  const loadSample = () => {
    const zero = Object.fromEntries(DENOMS.map((d) => [d.key, 0]));
    const sample = till === "Grill"
      ? {
          moneris_total: 420.55, tips: 58.0,
          register_drawer_total: 738.55, register_card_line: 362.55, register_cash_line: 376.0,
          tax_totals: { gst: 24.27, pst: 12.10, fd_pst: 8.40, liq_pst: 5.50 },
          denominations: { ...zero, b100: 4, b20: 5, b10: 2, b5: 1, loonie: 1 },
          group_totals: { green_fees: 0, pro_shop: 45.0, rentals: 30.0, restaurant: 210.0, alcohol: 95.0, beer: 40.55 },
          who_worked: "Sam & Jordan",
        }
      : till === "Masters Bar"
      ? {
          moneris_total: 640.0, tips: 0,
          register_drawer_total: 860.0, register_card_line: 640.0, register_cash_line: 220.0,
          tax_totals: { gst: 42.15, pst: 0, fd_pst: 0, liq_pst: 18.30 },
          denominations: { ...zero, b100: 4, b50: 1, b20: 3, b10: 1 },
          group_totals: { mb_d01: 320.0, mb_d02: 180.0, mb_d03: 360.0 },
          who_worked: "Alex",
        }
      : {
          moneris_total: 1850.0, tips: 0,
          register_drawer_total: 2270.0, register_card_line: 1850.0, register_cash_line: 420.0,
          tax_totals: { gst: 96.40, pst: 0, fd_pst: 0, liq_pst: 0 },
          denominations: { ...zero, b100: 5, b50: 2, b20: 5, b10: 1, b5: 2 },
          group_totals: { memberships: 0, green_fees: 980.0, cart_rental: 320.0, club_rental: 0, range_ball: 60.0, misc_golf: 0, food: 210.0, pop: 35.0, beer: 245.0 },
          who_worked: "Riley",
        };
    setData({ ...blank, ...sample, denominations: { ...blank.denominations, ...sample.denominations } });
    setI(flow.indexOf("total"));
    toast.success("Sample numbers loaded — balanced & ready to save");
  };

  const startAnother = () => {
    // Reset state and jump back to till/mode picker without leaving the app
    setData({ ...blank, who_worked: getName() || "" });
    setSavedId(null);
    setI(0);
    nav("/");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        till,
        mode,
        moneris_total: data.moneris_total,
        tips: data.tips,
        register_drawer_total: data.register_drawer_total,
        register_card_line: data.register_card_line,
        register_cash_line: data.register_cash_line,
        tax_totals: data.tax_totals,
        denominations: data.denominations,
        cheques: data.cheques,
        gift_cards: data.gift_cards,
        other: data.other,
        notes: data.notes,
        cash_tips_in_jar: data.cash_tips_in_jar,
        float_counted_back: data.float_counted_back,
        moneris_photo_b64: data.moneris_photo_b64,
        register_photo_b64: data.register_photo_b64,
        who_worked: data.who_worked,
        tips_paid_out: !!data.tips_paid_out,
        shortage_reason: data.shortage_reason,
        shortage_reason_other: data.shortage_reason_other,
        shortage_checklist: data.shortage_checklist,
        needs_review: !!data.needs_review || data.shortage_reason === "tips_from_till",
        closed_on: data.closed_on || null,
        float_in_count: data.float_in_count !== false,
        group_totals: data.group_totals,
        groups_photo_b64: data.groups_photo_b64,
        groups_photo_skip_reason: data.groups_photo_skip_reason || "",
      };
      const r = await createClose(payload);
      setSavedId(r.id);
      toast.success("Close saved");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  const summaryText = () => {
    const L = [
      `Till Trax — ${TILL_LABEL[till]}`,
      `${new Date().toLocaleString("en-CA")}`,
      ``,
      `Moneris total: ${money(data.moneris_total)}`,
      `Tips: ${money(data.tips)}`,
      `Card sales (Moneris − Tips): ${money(card_sales)}`,
      `Register card line (${CARDLINE[till]}): ${money(data.register_card_line)}  ${Math.abs(card_diff) <= 0.05 ? "✓ match" : "⚠ off"}`,
      `Cash counted${data.float_in_count === false ? " (deposit only — float set aside)" : " (full till)"}: ${money(cash_counted)}`,
      data.float_in_count === false ? `Float set aside earlier: ${money(floatAmt)}` : `Float kept back: ${money(floatAmt)}`,
      `Cash deposit: ${money(cash_deposit)}`,
      `Cheques: ${money(data.cheques)}`,
      `Gift cards / AG Bucks: ${money(data.gift_cards)}`,
      `Other (e-transfer etc.): ${money(data.other)}`,
      ``,
      `TOTAL [1]: ${money(total_one)}`,
      `Drawer total (${ZLINE[till]}): ${money(data.register_drawer_total)}`,
      `Difference: ${money(difference)} ${difference === 0 ? "(balanced)" : difference > 0 ? "OVER" : "SHORT"}`,
    ];
    if (data.notes) L.push(``, `Note: ${data.notes}`);
    return L.join("\n");
  };

  const share = async () => {
    const text = summaryText();
    try {
      if (navigator.share) {
        await navigator.share({ title: "Till Trax", text });
        return;
      }
    } catch { /* user cancelled or share failed — fall through to copy */ }
    const ok = await copyText(text);
    if (ok) toast.success("Copied to clipboard");
  };

  const copy = async () => {
    const ok = await copyText(summaryText());
    if (ok) toast.success("Copied to clipboard");
  };

  // -------- render each step --------
  const headerTitle = `${TILL_LABEL[till]} · ${mode.replace(/_/g, " ")}`;

  const renderStep = () => {
    switch (step) {
      case "intro":
        return (
          <StepShell
            testId="step-intro"
            title="Before you start"
            hint={`On the register, run the Z report (not the X — Z closes the day). Then on the Moneris terminal (${TERMINAL[till]}), print the daily summary — pick THIS till only, not "All".`}
            footer={
              <div className="space-y-2">
                <PrimaryBtn testId="intro-next" onClick={next}>I have both receipts <ChevronRight size={18} /></PrimaryBtn>
                <GhostBtn testId="intro-sample" onClick={loadSample}>No receipt handy? Fill with sample numbers</GhostBtn>
              </div>
            }
          >
            <div className="bg-[#F5F2EA] rounded-2xl p-5 space-y-3 border border-[#E8E4DA]">
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-full bg-[#1A5D3A] text-white flex items-center justify-center text-sm font-semibold shrink-0">1</div>
                <div className="text-[#2B533E]">Run the <strong>Z report</strong> on the {TILL_LABEL[till]} register.</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-full bg-[#1A5D3A] text-white flex items-center justify-center text-sm font-semibold shrink-0">2</div>
                <div className="text-[#2B533E]">Print the <strong>Moneris daily summary</strong> for terminal <span className="font-mono">{TERMINAL[till]}</span>.</div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-[#E8E4DA] p-4 mt-4" data-testid="past-day-card">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[#0D311E] font-medium text-sm">Logging a past day?</div>
                  <div className="text-xs text-[#5C7566]">Pick the date the till was actually closed.</div>
                </div>
                <input
                  data-testid="intro-closed-on"
                  type="date"
                  max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)}
                  value={data.closed_on || ""}
                  onChange={(e) => set({ closed_on: e.target.value })}
                  className="h-10 px-3 rounded-xl border border-[#E8E4DA] bg-[#FDFBF7] text-[#0D311E] text-sm outline-none focus:border-[#1A5D3A] shrink-0"
                />
              </div>
              {data.closed_on && (
                <div className="flex items-center justify-between mt-3 bg-[#D97706]/10 rounded-xl px-3 py-2">
                  <span className="text-xs text-[#92520A]" data-testid="past-day-note">
                    This close will be logged for <strong>{data.closed_on}</strong>.
                  </span>
                  <button
                    data-testid="past-day-clear"
                    onClick={() => set({ closed_on: "" })}
                    className="text-xs text-[#2B533E] underline shrink-0 ml-2"
                  >
                    Use today
                  </button>
                </div>
              )}
            </div>
          </StepShell>
        );

      case "moneris_photo":
        return (
          <StepShell
            testId="step-moneris-photo"
            title="Photograph the Moneris summary"
            hint={till === "Masters Bar"
              ? "The Masters Bar shares the Grill's Moneris terminal — no separate summary prints for the bar. Save your card receipts for the owner, and if you know your card total, tap Skip and type it in. Otherwise just skip."
              : "Tap the big box below — your phone's camera opens. Get the whole receipt in the shot. You'll confirm the numbers next."}
          >
            <PhotoStep
              kind="moneris"
              till={till}
              testIdPrefix="moneris"
              onResult={({ photo, ocr }) => {
                set({
                  moneris_photo_b64: photo,
                  moneris_total: ocr.moneris_total ?? data.moneris_total,
                  tips: ocr.tips ?? data.tips,
                });
                next();
              }}
              onSkip={() => next()}
            />
          </StepShell>
        );

      case "moneris_confirm":
        return (
          <StepShell
            testId="step-moneris-confirm"
            title="Does this look right?"
            hint="Tap a number to edit. Nothing is used until you confirm."
            footer={<PrimaryBtn testId="moneris-confirm-next" onClick={next}>Looks right <ChevronRight size={18} /></PrimaryBtn>}
          >
            {data.moneris_photo_b64 && (
              <img src={data.moneris_photo_b64} alt="" className="w-full max-h-40 object-contain bg-[#F5F2EA] rounded-xl border border-[#E8E4DA] mb-3" />
            )}
            <div className="space-y-3">
              <MoneyField label="Moneris Total (Volume)" testId="moneris-total" value={data.moneris_total} onChange={(v) => set({ moneris_total: v })} />
              <MoneyField label="Tips total" testId="moneris-tips" value={data.tips} onChange={(v) => set({ tips: v })} hint="Tips are paid in cash from the tip float box — never out of the till." />
              {Number(data.tips) > 0 && (
                <div data-testid="tips-warning" className="rounded-2xl bg-[#B91C1C]/10 border border-[#B91C1C]/30 p-4 text-[#7F1D1D]">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-semibold">Heads up</div>
                  <p className="text-sm mt-1">
                    The <strong>${Number(data.tips).toFixed(2)}</strong> in card tips gets paid in cash from the
                    locked <strong>tip float box ($375)</strong> — <u>not</u> out of the till. The paper
                    daily-balance sheet says "tips to be taken out of cash"; ignore that line.
                  </p>
                </div>
              )}
            </div>
          </StepShell>
        );

      case "register_photo":
        return (
          <StepShell
            testId="step-register-photo"
            title="Photograph the register Z tape"
            hint={`Tap the big box below — your camera opens. We're looking for the ${ZLINE[till]} line (drawer total) and the ${CARDLINE[till]} line (card sales).`}
          >
            <PhotoStep
              kind="register"
              till={till}
              testIdPrefix="register"
              onResult={({ photo, ocr }) => {
                const taxes = {};
                ["gst","pst","fd_pst","liq_pst"].forEach(k => { if (ocr?.[k] != null) taxes[k] = ocr[k]; });
                set({
                  register_photo_b64: photo,
                  register_drawer_total: ocr.drawer_total ?? data.register_drawer_total,
                  register_card_line: ocr.card_line ?? data.register_card_line,
                  register_cash_line: ocr.cash_line ?? data.register_cash_line,
                  tax_totals: { ...data.tax_totals, ...taxes },
                });
                next();
              }}
              onSkip={() => next()}
            />
          </StepShell>
        );

      case "register_confirm":
        return (
          <StepShell
            testId="step-register-confirm"
            title="Confirm the register lines"
            hint={`The drawer-total line is what we balance against — not the sales total. The card line is ALL cards (Visa, MasterCard, Debit), even though it's labelled "VISA".`}
            footer={<PrimaryBtn testId="register-confirm-next" onClick={next}>Looks right <ChevronRight size={18} /></PrimaryBtn>}
          >
            {data.register_photo_b64 && (
              <img src={data.register_photo_b64} alt="" className="w-full max-h-40 object-contain bg-[#F5F2EA] rounded-xl border border-[#E8E4DA] mb-3" />
            )}
            <div className="space-y-3">
              <MoneyField label={`Drawer total — ${ZLINE[till]}`} testId="register-drawer" value={data.register_drawer_total} onChange={(v) => set({ register_drawer_total: v })} />
              <MoneyField label={`Card line — ${CARDLINE[till]} (all cards)`} testId="register-card-line" value={data.register_card_line} onChange={(v) => set({ register_card_line: v })} hint="Visa + MasterCard + Debit combined — don't add MC again." />
              <MoneyField label="Cash line — CASH from Z tape" testId="register-cash-line" value={data.register_cash_line} onChange={(v) => set({ register_cash_line: v })} hint="What the register thinks the cash sales were. We'll check this against your counted cash." />
            </div>
            <div className="mt-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#5C7566] mb-2">Tax collected (optional)</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: "gst",    l: "GST" },
                  { k: "pst",    l: "PST" },
                  { k: "fd_pst", l: "FD-PST" },
                  { k: "liq_pst",l: "LIQ-PST" },
                ].map(({k,l}) => (
                  <MoneyField
                    key={k}
                    label={l}
                    testId={`tax-${k}`}
                    value={data.tax_totals?.[k] || 0}
                    onChange={(v) => set({ tax_totals: { ...data.tax_totals, [k]: v } })}
                  />
                ))}
              </div>
            </div>
          </StepShell>
        );

      case "departments": {
        const depts = DEPARTMENTS[till] || DEPARTMENTS["Grill"];
        // Only show user-added custom_* entries — D-codes stay on Pro Shop, G-codes stay on Grill.
        const customEntries = Object.entries(data.group_totals || {})
          .filter(([k]) => k.startsWith("custom_"));
        const total = depts.reduce((s, d) => s + (Number(data.group_totals?.[d.key]) || 0), 0)
                     + customEntries.reduce((s, [, v]) => s + (Number(v) || 0), 0);

        const addCustom = () => {
          const label = window.prompt("Department name (e.g. 'D07 Lessons')");
          if (!label) return;
          const key = "custom_" + label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
          set({ group_totals: { ...data.group_totals, [key]: 0, [`_label_${key}`]: label } });
        };

        return (
          <StepShell
            testId="step-departments"
            title="Departments from the Z tape"
            hint={till === "Grill"
              ? "Tap the camera box to snap the GROUP section of the tape (GREEN FEES, RESTAURANT SALES…) — the lines fill in automatically. Or type each one."
              : "Tap the camera box to snap the DEPT section of the tape (D01 → D15) — the lines fill in automatically. Or type each one. If a code isn't listed, tap 'Add unknown dept'."}
            footer={<PrimaryBtn testId="departments-next" onClick={() => {
              const hasReason = (data.groups_photo_skip_reason || "").trim().length >= 5;
              if (!data.groups_photo_b64 && !hasReason) {
                setDeptPhotoWarned(true);
                toast.error(deptPhotoWarned
                  ? "Type at least a few words explaining why there's no photo"
                  : "Photo required — snap the " + (till === "Grill" ? "GROUP" : "DEPT") + " section of the Z tape first");
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
              }
              if (total === 0 && !deptZeroWarned) { setDeptZeroWarned(true); return; }
              next();
            }}>Continue <ChevronRight size={18} /></PrimaryBtn>}
          >
            <PhotoStep
              kind="register_groups"
              till={till}
              testIdPrefix="groups"
              allowSkip={false}
              onResult={({ photo, ocr }) => {
                setDeptPhotoWarned(false);
                set({
                  groups_photo_b64: photo,
                  group_totals: { ...data.group_totals, ...Object.fromEntries(Object.entries(ocr || {}).filter(([, v]) => v !== null && v !== undefined)) },
                });
              }}
              onSkip={() => { /* stay on this step so user can type */ }}
            />
            <div className="space-y-2 mt-3">
              {depts.map((d) => (
                <MoneyField
                  key={d.key}
                  label={`${d.label}${d.code ? "  ·  " + d.code : d.group ? "  ·  G" + d.group : ""}`}
                  testId={`group-${d.key}`}
                  value={data.group_totals?.[d.key] || 0}
                  onChange={(v) => set({ group_totals: { ...data.group_totals, [d.key]: v } })}
                />
              ))}
              {customEntries.map(([k, v]) => (
                <MoneyField
                  key={k}
                  label={data.group_totals?.[`_label_${k}`] || k.replace(/^custom_/, "").replace(/_/g, " ")}
                  testId={`group-${k}`}
                  value={v || 0}
                  onChange={(nv) => set({ group_totals: { ...data.group_totals, [k]: nv } })}
                />
              ))}
            </div>
            <button
              data-testid="add-custom-dept"
              onClick={addCustom}
              className="w-full mt-2 h-11 rounded-xl border border-dashed border-[#1A5D3A]/40 text-[#2B533E] text-sm flex items-center justify-center gap-2 active:scale-[0.99]"
            >
              + Add unknown dept
            </button>
            <div className="mt-3 bg-[#F5F2EA] border border-[#E8E4DA] rounded-xl p-3 flex items-center justify-between" data-testid="dept-total">
              <span className="text-sm text-[#5C7566]">Department total</span>
              <span className="tabular-nums text-xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>{money(total)}</span>
            </div>
            {deptPhotoWarned && !data.groups_photo_b64 && (
              <div data-testid="dept-photo-warning" className="mt-3 bg-[#B3261E]/10 border border-[#B3261E]/30 rounded-xl p-3 text-sm text-[#8C1D18] space-y-2">
                <div>
                  <strong>Photo of the tape is required.</strong> Scroll up and tap the camera box to snap the {till === "Grill" ? "GROUP" : "DEPT"} section of the Z tape before continuing.
                </div>
                <div className="text-xs">
                  Truly can't take it (out of tape paper, camera broken…)? Type why and you can continue — the reason is saved with the close.
                </div>
                <input
                  data-testid="dept-photo-skip-reason"
                  value={data.groups_photo_skip_reason || ""}
                  onChange={(e) => set({ groups_photo_skip_reason: e.target.value })}
                  placeholder="Why no photo? e.g. register out of tape paper"
                  className="w-full h-11 rounded-xl border border-[#B3261E]/30 px-3 bg-white text-[#0D311E] text-sm outline-none focus:border-[#B3261E]"
                  autoComplete="off"
                />
                {(data.groups_photo_skip_reason || "").trim().length >= 5 && (
                  <div className="text-xs">Reason noted — tap Continue below.</div>
                )}
              </div>
            )}
            {deptZeroWarned && total === 0 && (
              <div data-testid="dept-zero-warning" className="mt-3 bg-[#D97706]/10 border border-[#D97706]/30 rounded-xl p-3 text-sm text-[#92520A]">
                <strong>Departments are still $0.00.</strong> Tap the camera box at the top to snap the {till === "Grill" ? "GROUP" : "DEPT"} section of the Z tape — the lines fill in by themselves. Or type each line. If the tape truly shows no department sales, tap Continue again.
              </div>
            )}
          </StepShell>
        );
      }

      case "card_check": {
        const ok = Math.abs(card_diff) <= 0.05;
        return (
          <StepShell
            testId="step-card-check"
            title="Card check"
            hint="Card sales come from the Moneris total minus tips. They should match the register's card line."
            footer={
              <div className="space-y-2">
                <PrimaryBtn testId="card-check-next" onClick={next}>{ok ? "Card matches — continue" : "Continue anyway"} <ChevronRight size={18} /></PrimaryBtn>
                {!ok && <GhostBtn testId="card-check-recheck" onClick={() => setI(flow.indexOf("register_confirm"))}>Re-check the numbers</GhostBtn>}
              </div>
            }
          >
            <div className="bg-white rounded-2xl border border-[#E8E4DA] p-5 space-y-3">
              <div className="flex justify-between">
                <span className="text-[#5C7566]">Moneris Total</span>
                <span className="tabular-nums" style={{ fontFamily: "'Fraunces', serif" }}>{money(data.moneris_total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C7566]">− Tips</span>
                <span className="tabular-nums" style={{ fontFamily: "'Fraunces', serif" }}>{money(data.tips)}</span>
              </div>
              <div className="h-px bg-[#E8E4DA]" />
              <div className="flex justify-between text-[#0D311E]">
                <span className="font-medium">Card sales</span>
                <span className="tabular-nums text-xl" style={{ fontFamily: "'Fraunces', serif" }} data-testid="card-sales-value">{money(card_sales)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C7566]">Register {CARDLINE[till]}</span>
                <span className="tabular-nums" style={{ fontFamily: "'Fraunces', serif" }}>{money(data.register_card_line)}</span>
              </div>
            </div>
            {ok ? (
              <div data-testid="card-check-ok" className="mt-4 rounded-xl bg-[#1B7B4B]/10 border border-[#1B7B4B]/20 text-[#155F39] p-4 flex items-start gap-3">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" />
                <div>Looks good — the card sales match the register.</div>
              </div>
            ) : (
              <div data-testid="card-check-warn" className="mt-4 rounded-xl bg-[#D97706]/10 border border-[#D97706]/25 text-[#92520A] p-4 flex items-start gap-3">
                <AlertTriangle size={20} className="shrink-0 mt-0.5" />
                <div>
                  Off by <strong>{money(Math.abs(card_diff))}</strong>. A card sale was probably rung in as cash, or missed —
                  write a quick note on the back of the sheet. Nobody's in trouble for an honest note.
                </div>
              </div>
            )}
          </StepShell>
        );
      }

      case "cash":
        return (
          <StepShell
            testId="step-cash"
            title="Count the cash drawer"
            hint={isCloseMode
              ? (data.float_in_count !== false
                ? `Count the whole till — float and all. We'll subtract the $${floatAmt} float to get the deposit.`
                : `Counting just the deposit — the $${floatAmt} float is already set aside.`)
              : "Tap + and − for each denomination."}
            footer={<PrimaryBtn testId="cash-next" onClick={next}>Continue <ChevronRight size={18} /></PrimaryBtn>}
          >
            {isCloseMode && (
              <>
                <div data-testid="float-mode-toggle" className="mb-3">
                  <div className="text-xs text-[#5C7566] mb-1.5">How are you counting?</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      data-testid="float-mode-in"
                      onClick={() => set({ float_in_count: true })}
                      className={`h-14 rounded-xl text-xs px-2 border transition-colors ${data.float_in_count !== false ? "bg-[#1A5D3A] text-white border-[#1A5D3A]" : "bg-white text-[#2B533E] border-[#E8E4DA]"}`}
                    >
                      Whole till — float still in
                    </button>
                    <button
                      data-testid="float-mode-out"
                      onClick={() => set({ float_in_count: false })}
                      className={`h-14 rounded-xl text-xs px-2 border transition-colors ${data.float_in_count === false ? "bg-[#1A5D3A] text-white border-[#1A5D3A]" : "bg-white text-[#2B533E] border-[#E8E4DA]"}`}
                    >
                      Float set aside first — counting the deposit
                    </button>
                  </div>
                </div>
                <div data-testid="float-reminder" className="mb-4 bg-[#D97706]/10 border border-[#D97706]/30 rounded-2xl p-4 text-sm text-[#92520A]">
                  {data.float_in_count !== false ? (
                    <>
                      <strong>Remember:</strong> this cash is the <strong>total till — float and all</strong>. Leave the {money(floatAmt)} float in the drawer and count everything together. The app takes the float off for you.
                    </>
                  ) : (
                    <>
                      <strong>Float set aside:</strong> you've already built the {money(floatAmt)} float and put it away. Count <strong>only the deposit pile</strong> here — nothing gets subtracted.
                    </>
                  )}
                </div>
              </>
            )}
            <CashCounter value={data.denominations} onChange={(v) => set({ denominations: v })} />
            {isCloseMode && (
              <div className="mt-4 bg-white rounded-xl border border-[#E8E4DA] p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-[#5C7566]">{data.float_in_count !== false ? "Float kept back" : "Float set aside earlier (not in count)"}</span><span className="tabular-nums">{money(floatAmt)}</span></div>
                <div className="flex justify-between"><span className="text-[#0D311E] font-medium">Cash deposit</span><span data-testid="cash-deposit-value" className="tabular-nums text-xl" style={{ fontFamily: "'Fraunces', serif" }}>{money(cash_deposit)}</span></div>
                {Number(data.register_cash_line) > 0 && (() => {
                  const expectedCash = Number(data.register_cash_line);
                  const cashDiff = cash_deposit - expectedCash;
                  const balanced = Math.abs(cashDiff) < 0.005;
                  const floatIn = data.float_in_count !== false;
                  const floatMissing = !balanced && cash_counted > 0 && floatIn && Math.abs(cashDiff + floatAmt) < 5;
                  const floatStillIn = !balanced && cash_counted > 0 && !floatIn && Math.abs(cashDiff - floatAmt) < 5;
                  return (
                    <div data-testid="cash-check" className={`mt-2 rounded-xl p-3 text-sm ${balanced ? "bg-[#1B7B4B]/10 text-[#155F39]" : "bg-[#D97706]/10 text-[#92520A]"}`}>
                      <div className="flex justify-between"><span>Z tape CASH</span><span className="tabular-nums">{money(expectedCash)}</span></div>
                      <div className="flex justify-between"><span>{floatIn ? "Counted − float" : "Counted (deposit only)"}</span><span className="tabular-nums">{money(cash_deposit)}</span></div>
                      <div className="flex justify-between font-medium mt-1 pt-1 border-t border-current/20"><span>{balanced ? "Cash balances ✓" : (cashDiff > 0 ? "Cash OVER" : "Cash SHORT")}</span><span className="tabular-nums">{money(Math.abs(cashDiff))}</span></div>
                      {floatMissing && (
                        <div data-testid="float-missing-nudge" className="mt-2 pt-2 border-t border-current/20 text-[#B91C1C]">
                          <strong>Hold on —</strong> you're short by almost exactly the float. Is the {money(floatAmt)} float still sitting outside the count? Either add those bills in, or tap "Float set aside first" above if you built the float before counting.
                        </div>
                      )}
                      {floatStillIn && (
                        <div data-testid="float-still-in-nudge" className="mt-2 pt-2 border-t border-current/20 text-[#B91C1C]">
                          <strong>Hold on —</strong> you're over by almost exactly the float. It looks like the {money(floatAmt)} float is still in this pile. Either pull it out, or tap "Whole till" above if you counted everything together.
                        </div>
                      )}
                    </div>
                  );
                })()}
                {mode === "shift_change" && (
                  <div className="text-xs text-[#5C7566] mt-1">Shift change: the cash stays in the drawer for the next person — no envelope.</div>
                )}
              </div>
            )}
          </StepShell>
        );

      case "extras":
        return (
          <StepShell
            testId="step-extras"
            title="Anything else in the till?"
            hint="If none, leave them at $0.00."
            footer={<PrimaryBtn testId="extras-next" onClick={next}>Continue <ChevronRight size={18} /></PrimaryBtn>}
          >
            <div className="space-y-3">
              <MoneyField label="Cheques" testId="extras-cheques" value={data.cheques} onChange={(v) => set({ cheques: v })} />
              <MoneyField label="AGC Gift cards / AG Bucks" testId="extras-gift" value={data.gift_cards} onChange={(v) => set({ gift_cards: v })} />
              <MoneyField label="E-transfer / other" testId="extras-other" value={data.other} onChange={(v) => set({ other: v })} />
            </div>
          </StepShell>
        );

      case "total": {
        const over = difference > 0;
        const balanced = Math.abs(difference) < 0.005;
        const big = Math.abs(difference) > 10;
        const huge = Math.abs(difference) > 50;
        const tone = balanced ? "balanced" : big ? "big" : "small";
        const toneCfg = {
          balanced: { color: "text-[#1B7B4B]", bg: "bg-[#1B7B4B]/10", label: "Balanced" },
          small:    { color: "text-[#92520A]", bg: "bg-[#D97706]/10", label: over ? "OVER" : "SHORT" },
          big:      { color: "text-[#B91C1C]", bg: "bg-[#B91C1C]/10", label: over ? "OVER" : "SHORT" },
        }[tone];
        // Auto-flag big variances for owner review on first visit to this step.
        if (huge && !data.needs_review) set({ needs_review: true });
        return (
          <StepShell
            testId="step-total"
            title="Here's the math"
            footer={<PrimaryBtn testId="total-next" onClick={next}>Continue <ChevronRight size={18} /></PrimaryBtn>}
          >
            <div className="bg-white rounded-2xl border border-[#E8E4DA] p-5 space-y-2">
              {[
                ["Card sales", card_sales],
                ["Cash deposit", cash_deposit],
                ["Cheques", data.cheques],
                ["Gift cards", data.gift_cards],
                ["Other", data.other],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between text-[#2B533E]">
                  <span>{k}</span>
                  <span className="tabular-nums">{money(v)}</span>
                </div>
              ))}
              <div className="h-px bg-[#E8E4DA] my-2" />
              <div className="flex justify-between text-[#0D311E]">
                <span className="font-medium">TOTAL [1]</span>
                <span data-testid="total-one" className="tabular-nums text-xl" style={{ fontFamily: "'Fraunces', serif" }}>{money(total_one)}</span>
              </div>
              <div className="flex justify-between text-[#5C7566] text-sm">
                <span>Drawer total ({ZLINE[till]})</span>
                <span className="tabular-nums">{money(data.register_drawer_total)}</span>
              </div>
            </div>

            <div className={`mt-4 rounded-2xl ${toneCfg.bg} p-6 text-center border border-[#E8E4DA]`} data-testid="diff-card">
              <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase mb-1">{toneCfg.label}</div>
              <div
                data-testid="diff-amount"
                className={`text-5xl sm:text-6xl tabular-nums ${toneCfg.color}`}
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                {money(Math.abs(difference))}
              </div>
              <div className="text-xs text-[#5C7566] mt-3">
                {balanced ? "Right on the money."
                  : huge ? "Way out of line. Take a breath — let's slow down and check a few things below before saving."
                  : big ? "Over $10 either way — write a quick note on the back. Nobody's in trouble for an honest note."
                  : "Small variance is normal. A short note helps next time."}
              </div>
            </div>

            {huge && (
              <div data-testid="huge-variance" className="mt-3 bg-[#B91C1C]/10 border border-[#B91C1C]/30 rounded-2xl p-4 text-[#7F1D1D] space-y-2">
                <div className="text-[10px] tracking-[0.22em] uppercase font-semibold">Way off — before you save</div>
                <ol className="list-decimal pl-5 space-y-1 text-sm">
                  <li><strong>Recount the cash drawer.</strong> Most big variances are miscounts.</li>
                  <li><strong>Re-photograph the Moneris and Z tape</strong> — make sure you read the right lines (DRWR TTL, not NET SALE).</li>
                  <li><strong>Check tips weren't taken out of the till cash.</strong> Tip payouts come from the $375 tip float box.</li>
                  <li><strong>Don't add or remove cash from the drawer to balance.</strong> Save what you actually counted and write a note.</li>
                </ol>
                <p className="text-xs text-[#7F1D1D]/80 mt-1">This close will be auto-flagged for owner review when you save.</p>
              </div>
            )}

            {big && (
              <div className="mt-3 bg-white rounded-2xl border border-[#E8E4DA] p-4">
                <label className="text-xs tracking-[0.18em] uppercase text-[#5C7566]">Note (optional)</label>
                <textarea
                  data-testid="notes-input"
                  rows={3}
                  value={data.notes}
                  onChange={(e) => set({ notes: e.target.value })}
                  className="w-full mt-2 rounded-xl border border-[#E8E4DA] p-3 bg-[#FDFBF7] text-[#0D311E] outline-none focus:border-[#1A5D3A]"
                  placeholder="e.g. card sale rung in as cash on hole 14"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            )}
          </StepShell>
        );
      }

      case "reason": {
        const balanced = Math.abs(difference) < 0.005;
        if (balanced) {
          return (
            <StepShell
              testId="step-reason"
              title="Right on the money"
              hint="No variance to explain. Carry on."
              footer={<PrimaryBtn testId="reason-next" onClick={next}>Continue <ChevronRight size={18} /></PrimaryBtn>}
            />
          );
        }
        const over = difference > 0;
        const big = Math.abs(difference) > 10;
        const mustPick = big && !data.shortage_reason;
        return (
          <StepShell
            testId="step-reason"
            title={over ? "Why might it be over?" : "Why might it be short?"}
            hint={big
              ? `Off by ${money(Math.abs(difference))} — pick the closest reason before continuing. Nobody's in trouble, this is just to spot patterns.`
              : "Pick the closest reason. Add a quick note if it helps."}
            footer={<PrimaryBtn testId="reason-next" onClick={next} disabled={mustPick}>Continue <ChevronRight size={18} /></PrimaryBtn>}
          >
            <div className="space-y-2">
              {SHORTAGE_REASONS.map((r) => (
                <button
                  key={r.id}
                  data-testid={`reason-${r.id}`}
                  onClick={() => set({ shortage_reason: r.id })}
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${data.shortage_reason === r.id ? "bg-[#1A5D3A] text-white border-[#1A5D3A]" : "bg-white text-[#2B533E] border-[#E8E4DA] hover:border-[#1A5D3A]"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {data.shortage_reason === "tips_from_till" && (
              <div data-testid="tips-from-till-hint" className="mt-3 bg-[#B91C1C]/10 border border-[#B91C1C]/30 rounded-2xl p-4 text-[#7F1D1D]">
                <p className="text-sm">
                  That's almost certainly it. The till is off by <strong>{money(Math.abs(difference))}</strong> and the
                  card tips total was <strong>{money(data.tips)}</strong>. Save the close as it actually counted — the
                  app will flag it. For next shift: <strong>card tips come from the locked tip float box ($375)</strong>,
                  never the till.
                </p>
              </div>
            )}
            <div className="mt-3 bg-white rounded-xl border border-[#E8E4DA] p-3">
              <label className="text-[10px] tracking-[0.18em] uppercase text-[#5C7566]">Note (optional)</label>
              <textarea
                data-testid="reason-other"
                rows={3}
                value={data.shortage_reason_other}
                onChange={(e) => set({ shortage_reason_other: e.target.value })}
                placeholder="What happened? e.g. did a refund instead of a void on hole 14"
                className="w-full mt-2 rounded-xl border border-[#E8E4DA] p-2 bg-[#FDFBF7] text-[#0D311E] outline-none focus:border-[#1A5D3A]"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
          </StepShell>
        );
      }

      case "checks": {
        const short = difference < -0.005;
        return (
          <StepShell
            testId="step-checks"
            title={short ? "If the till is short — run through these" : "Quick checks"}
            hint={short ? "Tick the ones you've already thought about. It's fine if you can't tick them all." : "Take a beat to confirm these two."}
            footer={<PrimaryBtn testId="checks-next" onClick={next}>Continue <ChevronRight size={18} /></PrimaryBtn>}
          >
            <YesNo testIdRoot="check-tips" label="Cash tips dropped in the jar?" value={data.cash_tips_in_jar} onChange={(v) => set({ cash_tips_in_jar: v })} />
            <YesNo testIdRoot="check-float" label={`Float counted back to $${floatAmt}?`} value={data.float_counted_back} onChange={(v) => set({ float_counted_back: v })} />
            {short && (
              <div className="mt-2 bg-white rounded-2xl border border-[#E8E4DA] p-4 space-y-2" data-testid="shortage-checklist">
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#5C7566] mb-1">Shortage checks</div>
                {SHORTAGE_CHECKS.map((c) => {
                  const v = !!(data.shortage_checklist || {})[c.key];
                  return (
                    <label key={c.key} data-testid={`shortage-${c.key}`} className="flex items-start gap-3 p-2 rounded-lg hover:bg-[#F5F2EA] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={v}
                        onChange={(e) => set({ shortage_checklist: { ...(data.shortage_checklist || {}), [c.key]: e.target.checked } })}
                        className="mt-1 h-4 w-4 accent-[#1A5D3A]"
                      />
                      <span className="text-[#2B533E] text-sm">{c.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </StepShell>
        );
      }

      case "who_worked":
        return (
          <StepShell
            testId="step-who-worked"
            title="Who worked this shift?"
            hint="Their card tips get paid in cash from the tip float box."
            footer={<PrimaryBtn testId="who-next" onClick={next} disabled={!data.who_worked.trim()}>Continue <ChevronRight size={18} /></PrimaryBtn>}
          >
            <StaffPicker value={data.who_worked} onChange={(v) => set({ who_worked: v })} />

            {Number(data.tips) > 0 ? (() => {
              const names = data.who_worked.split(/\s*(?:,|&|\sand\s)\s*/i).map((n) => n.trim()).filter(Boolean);
              const share = names.length > 1 ? data.tips / names.length : 0;
              return (
                <div className="mt-3 bg-[#0D311E] text-[#FDFBF7] rounded-2xl p-5" data-testid="tip-payout-card">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#FDFBF7]/70">Pay out now — from the tip float box</div>
                  <div className="text-5xl tabular-nums mt-2" style={{ fontFamily: "'Fraunces', serif" }}>{money(data.tips)}</div>
                  <div className="text-sm text-[#FDFBF7]/80 mt-3">
                    Give <strong>{money(data.tips)}</strong> cash to <strong>{data.who_worked.trim() || "the staff who worked"}</strong> out of the shared tip float box (${TIP_FLOAT_BOX}). Lock it back up.
                  </div>
                  {names.length > 1 && (
                    <div className="mt-3 bg-[#FDFBF7]/10 rounded-xl p-3" data-testid="tip-split">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[#FDFBF7]/70 mb-2">Split evenly — {money(share)} each</div>
                      <div className="space-y-1">
                        {names.map((n) => (
                          <div key={n} className="flex justify-between text-sm">
                            <span>{n}</span>
                            <span className="tabular-nums">{money(share)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    <input
                      id="paid-toggle"
                      data-testid="tip-paid-toggle"
                      type="checkbox"
                      checked={!!data.tips_paid_out}
                      onChange={(e) => set({ tips_paid_out: e.target.checked })}
                      className="h-5 w-5 rounded border-[#FDFBF7]/40 bg-transparent accent-[#D19C4C]"
                    />
                    <label htmlFor="paid-toggle" className="text-sm">I just paid it out. Cash is gone from the box.</label>
                  </div>
                </div>
              );
            })() : (
              <div className="mt-3 bg-[#F5F2EA] rounded-xl border border-[#E8E4DA] p-4 text-sm text-[#5C7566]" data-testid="tip-payout-none">
                No card tips on this close — nothing to pay out of the tip float box.
              </div>
            )}
          </StepShell>
        );

      case "morning_summary":
        return (
          <StepShell
            testId="step-morning-summary"
            title="Morning count"
            hint={`Float is $${floatAmt}.`}
            footer={
              <div className="space-y-2">
                <PrimaryBtn testId="morning-save" onClick={save} disabled={saving || savedId}>{savedId ? "Saved ✓" : saving ? "Saving…" : "Save morning count"}</PrimaryBtn>
                {savedId && <GhostBtn testId="morning-new" onClick={startAnother}><Plus size={14} className="inline mr-1.5" /> Start another close</GhostBtn>}
                {savedId && <GhostBtn testId="back-home-2" onClick={() => nav("/")}>Done</GhostBtn>}
              </div>
            }
          >
            <Stat label="Cash counted" value={money(cash_counted)} />
            <Stat label="Float expected" value={money(floatAmt)} />
            <Stat label="Difference" value={money(cash_counted - floatAmt)} highlight />
          </StepShell>
        );

      case "just_count_summary":
        return (
          <StepShell
            testId="step-just-count-summary"
            title="Till total"
            hint="Nothing has been saved."
            footer={<PrimaryBtn testId="just-count-done" onClick={() => nav("/")}>Done</PrimaryBtn>}
          >
            <Stat label="Cash counted" value={money(cash_counted)} highlight />
          </StepShell>
        );

      case "summary":
        return (
          <StepShell
            testId="step-summary"
            title={savedId ? "Saved." : "Ready to save"}
            hint={savedId ? "It's in the dashboard. Copy or share the numbers if you want." : data.closed_on ? `Save sends it to the shared dashboard — logged for ${data.closed_on}.` : "Save sends it to the shared dashboard."}
            footer={
              savedId ? (
                <div className="space-y-2">
                  {Math.abs(difference) > 10 && <CoachButton closeId={savedId} label="Talk it through with the assistant" />}
                  <PrimaryBtn testId="summary-share" onClick={share}><Share2 size={16} /> Share</PrimaryBtn>
                  <GhostBtn testId="summary-new" onClick={startAnother}><Plus size={14} className="inline mr-1.5" /> Start another close</GhostBtn>
                  <GhostBtn testId="summary-copy" onClick={copy}><Copy size={14} className="inline mr-1.5" /> Copy summary</GhostBtn>
                  <GhostBtn testId="summary-home" onClick={() => nav("/")}>Back to start</GhostBtn>
                </div>
              ) : (
                <PrimaryBtn testId="summary-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save this close"}</PrimaryBtn>
              )
            }
          >
            <SummaryGrid data={data} till={till} card_sales={card_sales} cash_counted={cash_counted} cash_deposit={cash_deposit} total_one={total_one} difference={difference} floatAmt={floatAmt} mode={mode} />
          </StepShell>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header title={headerTitle} onBack={back} />
      {flow.length > 1 && (
        <div className="max-w-md mx-auto px-5 pt-3">
          <div className="h-1 bg-[#E8E4DA] rounded-full overflow-hidden">
            <div
              data-testid="wizard-progress"
              className="h-full bg-[#1A5D3A] transition-all"
              style={{ width: `${((i + 1) / flow.length) * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#5C7566]">Step {i + 1} of {flow.length}</div>
            {!savedId && (
              <button
                data-testid="wizard-start-over"
                onClick={startOver}
                className="text-[11px] tracking-[0.18em] uppercase text-[#5C7566] hover:text-[#0D311E] flex items-center gap-1.5"
              >
                <RotateCcw size={12} /> Start over
              </button>
            )}
          </div>
        </div>
      )}
      <main className="max-w-md mx-auto px-5 pb-24 pt-5">{renderStep()}</main>

      {!savedId && (
        <>
          <button
            data-testid="wizard-coach-fab"
            onClick={() => setCoachOpen(true)}
            className="fixed bottom-24 right-5 z-40 h-10 px-3.5 rounded-full bg-[#0D311E] text-[#FDFBF7] shadow-lg shadow-[#0D311E]/25 flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
          >
            <MessageSquare size={14} />
            <span className="text-xs font-medium">Ask</span>
          </button>
          <CoachDialog
            contextText={`Closing the ${TILL_LABEL[till]} till (${mode.replace(/_/g, " ")}). Current step: ${step}. Numbers so far — Moneris total: $${data.moneris_total}, Tips: $${data.tips}, Card sales (Moneris − tips): $${card_sales}, Register card line: $${data.register_card_line}, Drawer total: $${data.register_drawer_total}, Register cash line: $${data.register_cash_line}, Cash counted: $${cash_counted}, Float kept back: $${floatAmt}, Cash deposit: $${cash_deposit}, TOTAL[1]: $${total_one}, Difference: $${difference}. Reason picked: ${data.shortage_reason || "none"}. Help the staff member understand what to do next, in plain, blame-free language.`}
            open={coachOpen}
            onOpenChange={setCoachOpen}
          />
        </>
      )}
    </div>
  );
}

function YesNo({ label, value, onChange, testIdRoot }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E4DA] p-4 mb-3" data-testid={testIdRoot}>
      <div className="text-[#0D311E] mb-3">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        {[["yes", true], ["no", false]].map(([k, v]) => (
          <button
            key={k}
            data-testid={`${testIdRoot}-${k}`}
            onClick={() => onChange(v)}
            className={`h-12 rounded-xl border text-base ${value === v ? "bg-[#1A5D3A] text-white border-[#1A5D3A]" : "bg-[#F5F2EA] text-[#2B533E] border-[#E8E4DA]"}`}
          >
            {k === "yes" ? "Yes" : "Not yet"}
          </button>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`flex justify-between items-center rounded-xl p-4 mb-2 border ${highlight ? "bg-[#0D311E] text-[#FDFBF7] border-[#0D311E]" : "bg-white border-[#E8E4DA] text-[#0D311E]"}`}>
      <span className={highlight ? "text-[#FDFBF7]/70 text-sm" : "text-[#5C7566] text-sm"}>{label}</span>
      <span className="tabular-nums text-xl" style={{ fontFamily: "'Fraunces', serif" }}>{value}</span>
    </div>
  );
}

function SummaryGrid({ data, till, card_sales, cash_counted, cash_deposit, total_one, difference, floatAmt }) {
  const rows = [
    ["Moneris total", money(data.moneris_total)],
    ["Tips", money(data.tips)],
    ["Card sales", money(card_sales)],
    [`Register ${CARDLINE[till]}`, money(data.register_card_line)],
    [data.float_in_count === false ? "Cash counted (deposit only)" : "Cash counted (full till)", money(cash_counted)],
    [data.float_in_count === false ? "Float set aside earlier" : "Float kept back", money(floatAmt)],
    ["Cash deposit", money(cash_deposit)],
    ["Cheques", money(data.cheques)],
    ["Gift cards", money(data.gift_cards)],
    ["Other", money(data.other)],
    ["TOTAL [1]", money(total_one), true],
    [`Drawer total (${ZLINE[till]})`, money(data.register_drawer_total)],
    ["Difference", money(difference), true],
  ];
  return (
    <div className="bg-white rounded-2xl border border-[#E8E4DA] p-5 space-y-2" data-testid="summary-grid">
      {rows.map(([k, v, bold]) => (
        <div key={k} className={`flex justify-between ${bold ? "text-[#0D311E] text-lg pt-1" : "text-[#2B533E]"}`}>
          <span>{k}</span>
          <span className="tabular-nums" style={{ fontFamily: bold ? "'Fraunces', serif" : undefined }}>{v}</span>
        </div>
      ))}
    </div>
  );
}
